import React, { createContext, useContext, useState, useEffect } from 'react';
import { dataAPI } from '../../actions/imsServer';

const LevelsContext = createContext();

export const useLevels = () => {
    const context = useContext(LevelsContext);
    if (!context) {
        throw new Error('useLevels must be used within a LevelsProvider');
    }
    return context;
};

export const LevelsProvider = ({ children }) => {
    const [userLevels, setUserLevels] = useState(null);
    const [organizationLevels, setOrganizationLevels] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchLevels = async () => {
            try {
                setLoading(true);

                // 유저 레벨 데이터 가져오기
                const userLevelResponse = await dataAPI.getUserLevels();

                // API 응답 구조에 따라 userLevels 추출
                // 응답 구조: { result: 'ok', data: { userLevels: [...] } } 또는 { result: 'ok', data: [...] }
                let extractedUserLevels = null;

                if (userLevelResponse?.data) {
                    if (Array.isArray(userLevelResponse.data)) {
                        // { data: [...] } 형태
                        extractedUserLevels = userLevelResponse.data;
                    } else if (userLevelResponse.data.userLevels) {
                        // { data: { userLevels: [...] } } 형태
                        extractedUserLevels = userLevelResponse.data.userLevels;
                    }
                } else if (Array.isArray(userLevelResponse)) {
                    // 배열로 직접 반환된 경우
                    extractedUserLevels = userLevelResponse;
                } else if (userLevelResponse?.userLevels) {
                    // { userLevels: [...] } 형태
                    extractedUserLevels = userLevelResponse.userLevels;
                }

                if (extractedUserLevels && Array.isArray(extractedUserLevels)) {
                    setUserLevels(extractedUserLevels);
                } else {
                    console.warn('userLevels not found in response. Full response:', userLevelResponse);
                    console.warn('data structure:', userLevelResponse?.data);
                    // localStorage 캐시 확인
                    const cached = localStorage.getItem('userLevels');
                    if (cached) {
                        try {
                            const parsedCache = JSON.parse(cached);
                            console.log('Cached userLevels:', parsedCache);
                            // 캐시도 동일한 로직으로 파싱 시도
                            if (Array.isArray(parsedCache)) {
                                setUserLevels(parsedCache);
                            } else if (parsedCache?.data) {
                                if (Array.isArray(parsedCache.data)) {
                                    setUserLevels(parsedCache.data);
                                } else if (parsedCache.data.userLevels) {
                                    setUserLevels(parsedCache.data.userLevels);
                                }
                            } else if (parsedCache?.userLevels) {
                                setUserLevels(parsedCache.userLevels);
                            }
                        } catch (e) {
                            console.error('Failed to parse cached userLevels:', e);
                        }
                    }
                }

                // 기관 레벨 데이터 가져오기
                try {
                    const organizationLevelResponse = await dataAPI.getOrganizationLevels();
                    
                    // API 응답 구조에 따라 organizationLevels 추출
                    // 응답 형태: { result: 'ok', data: { organizationLevels: [...] } } 
                    let extractedOrgLevels = null;
                    
                    if (organizationLevelResponse) {
                        // { result: 'ok', data: { organizationLevels: [...] } } 형태
                        if (organizationLevelResponse.result === 'ok' && organizationLevelResponse.data?.organizationLevels) {
                            extractedOrgLevels = organizationLevelResponse.data.organizationLevels;
                        } 
                        // { data: { organizationLevels: [...] } } 형태
                        else if (organizationLevelResponse.data?.organizationLevels) {
                            extractedOrgLevels = organizationLevelResponse.data.organizationLevels;
                        } 
                        // { organizationLevels: [...] } 형태
                        else if (organizationLevelResponse.organizationLevels) {
                            extractedOrgLevels = organizationLevelResponse.organizationLevels;
                        } 
                        // 배열로 직접 반환된 경우
                        else if (Array.isArray(organizationLevelResponse)) {
                            extractedOrgLevels = organizationLevelResponse;
                        }
                    }
                    
                    // 추출된 값이 없으면 localStorage 캐시 확인
                    if (!extractedOrgLevels || !Array.isArray(extractedOrgLevels)) {
                        const cached = localStorage.getItem('organizationLevels');
                        if (cached) {
                            try {
                                const parsedCache = JSON.parse(cached);
                                // 캐시도 동일한 로직으로 파싱
                                if (Array.isArray(parsedCache)) {
                                    extractedOrgLevels = parsedCache;
                                } else if (parsedCache?.result === 'ok' && parsedCache?.data?.organizationLevels) {
                                    extractedOrgLevels = parsedCache.data.organizationLevels;
                                } else if (parsedCache?.data?.organizationLevels) {
                                    extractedOrgLevels = parsedCache.data.organizationLevels;
                                } else if (parsedCache?.organizationLevels) {
                                    extractedOrgLevels = parsedCache.organizationLevels;
                                }
                            } catch (e) {
                                console.error('Failed to parse cached organizationLevels:', e);
                            }
                        }
                    }
                    
                    if (extractedOrgLevels && Array.isArray(extractedOrgLevels)) {
                        setOrganizationLevels(extractedOrgLevels);
                    } else {
                        console.warn('organizationLevels not found or not an array. Response:', organizationLevelResponse);
                    }
                } catch (orgError) {
                    console.error('Failed to fetch organizationLevels:', orgError);
                }
            } catch (error) {
                console.error('Failed to fetch levels:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchLevels();
    }, []);

    return (
        <LevelsContext.Provider value={{ userLevels, organizationLevels, loading }}>
            {children}
        </LevelsContext.Provider>
    );
};

